import React, { createContext, useContext, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { NeckEditorProvider } from "../../contexts/NeckEditorContext";
import { NeckEditorOverlay } from "../NeckEditorOverlay";
import { BordonaAppBar } from "./BordonaAppBar";
import { getContextualThemeForModule, type ContextualTheme } from "../../lib/contextualTheme";
import { isExploreSandboxPath } from "../../lib/exploreSandbox";

// ─── Theme Constants — Serenidad con pulso ───
const C = {
  bgRoot: "#FDF8F0",           // Warm Ivory
  bgPanel: "#FFFFFF",          // Superficie flotante
  bgPanelHover: "#F5F0E8",
  border: "#E8E0D4",
  borderLight: "#F0EAE0",
  textMain: "#2C2825",
  textMuted: "#8A8078",
  textLight: "#B8A99A",
  accent: "#FF4D4D",
  appBar: "transparent",
  appBarLight: "#F5F0E8",
};

interface WorkbenchContextType {
  leftCollapsed: boolean;
  setLeftCollapsed: (v: boolean) => void;
  rightCollapsed: boolean;
  setRightCollapsed: (v: boolean) => void;
  bottomCollapsed: boolean;
  setBottomCollapsed: (v: boolean) => void;
  activeModule: string;
  setActiveModule: (v: string) => void;
  /** `learnCanvas`: fondo transparente para SongLearnCanvasBackdrop + UI flotante oscura. */
  surface: "workbench" | "learnCanvas";
  /** Tema contextual (Escalas / Modos / Acordes / Songbook / …). */
  theme: ContextualTheme;
  /** Vista sandbox desde /explore — sin switcher de módulos ni modo aprender/practicar. */
  isSandbox: boolean;
}

const defaultCtxTheme = getContextualThemeForModule("scales");

const WorkbenchContext = createContext<WorkbenchContextType>({
  leftCollapsed: false,
  setLeftCollapsed: () => {},
  rightCollapsed: false,
  setRightCollapsed: () => {},
  bottomCollapsed: true,
  setBottomCollapsed: () => {},
  activeModule: "scales",
  setActiveModule: () => {},
  surface: "workbench",
  theme: defaultCtxTheme,
  isSandbox: false,
});

export const useWorkbench = () => useContext(WorkbenchContext);

interface WorkbenchLayoutProps {
  children: React.ReactNode;
  sidebar?: React.ReactNode;
  /** Oculta por completo la columna izquierda (p. ej. piano con dock inferior). */
  hideLeftSidebar?: boolean;
  inspector?: React.ReactNode;
  bottom?: React.ReactNode;
  /** Dock fijo compacto: sin scroll ni barra “Colapsar” alta. */
  bottomDock?: boolean;
  title?: string;
  activeModule?: string;
  /** Con `learnCanvas`, el layout no pinta fondo crema: debe usarse dentro de SongLearnCanvasBackdrop. */
  surface?: "workbench" | "learnCanvas";
  /** Explore inmersivo: sin columnas laterales, contenido edge-to-edge */
  immersive?: boolean;
}

export const WorkbenchLayout: React.FC<WorkbenchLayoutProps> = ({
  children,
  sidebar,
  hideLeftSidebar = false,
  inspector,
  bottom,
  bottomDock = false,
  title,
  activeModule: initialModule = "scales",
  surface = "workbench",
  immersive = false,
}) => {
  const location = useLocation();
  const isSandbox = isExploreSandboxPath(location.pathname);

  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(!inspector);
  const [bottomCollapsed, setBottomCollapsed] = useState(bottomDock ? false : !bottom);
  const [activeModule, setActiveModule] = useState(initialModule);
  const theme = useMemo(() => getContextualThemeForModule(activeModule), [activeModule]);
  const accentEdge =
    theme.accent.length === 7 ? `${theme.accent}33` : theme.accent;

  const learn = surface === "learnCanvas";
  const serenity = isSandbox && (learn || immersive);
  const P = serenity
    ? {
        root: "transparent",
        panel: "transparent",
        panelHi: "transparent",
        border: "rgba(255,255,255,0.1)",
        muted: "rgba(255,255,255,0.5)",
        panelBar: "transparent",
        titleText: "rgba(248,250,252,0.95)",
        panelShadow: undefined,
        panelBorder: undefined,
        blur: undefined,
      }
    : {
        root: learn ? "transparent" : C.bgRoot,
        panel: learn ? "rgba(255,255,255,0.05)" : C.bgPanel,
        panelHi: learn ? "rgba(255,255,255,0.085)" : C.bgPanelHover,
        border: learn ? `rgba(255,255,255,0.08)` : C.borderLight,
        muted: learn ? "rgba(195,210,220,0.78)" : C.textMuted,
        panelBar: learn ? "rgba(255,255,255,0.04)" : C.bgPanel,
        titleText: learn ? "rgba(248,250,252,0.95)" : C.textMain,
        panelShadow: learn ? "0 20px 50px rgba(0,0,0,0.48)" : undefined,
        panelBorder: learn ? `1px solid ${accentEdge}` : undefined,
        blur: learn ? "blur(24px)" : undefined,
      };

  return (
    <NeckEditorProvider>
      <WorkbenchContext.Provider
        value={{
          leftCollapsed,
          setLeftCollapsed,
          rightCollapsed,
          setRightCollapsed,
          bottomCollapsed,
          setBottomCollapsed,
          activeModule,
          setActiveModule,
          surface,
          theme,
          isSandbox,
        }}
      >
        <div
          className="h-[100dvh] w-full max-w-[100vw] flex flex-col overflow-hidden relative transition-all duration-700"
          style={
            {
              backgroundColor: surface === "learnCanvas" ? "transparent" : C.bgRoot,
              "--bordona-accent": theme.accent,
              "--bordona-accent-hover": theme.accentHover,
              "--bordona-accent-soft": theme.accent.length === 7 ? `${theme.accent}1A` : theme.accent,
              "--bordona-accent-border": accentEdge,
            } as React.CSSProperties
          }
        >
          <BordonaAppBar exploreBack={isSandbox} />
          <div
            className={`flex-1 flex min-w-0 overflow-hidden ${
              immersive ? "p-0" : "p-3 gap-3"
            }`}
            style={{ backgroundColor: P.root }}
          >
            {/* Left Sidebar */}
            {!hideLeftSidebar && (
            <div
              className={`flex flex-col transition-all duration-700 ease-out overflow-hidden shrink-0 ${
                serenity ? "border-r border-white/10" : "rounded-2xl shadow-lg"
              }`}
              style={{
                width: leftCollapsed ? 48 : 280,
                backgroundColor: P.panel,
                border: serenity ? undefined : P.panelBorder,
                boxShadow: P.panelShadow,
                backdropFilter: P.blur,
              }}
            >
              <button
                onClick={() => setLeftCollapsed(!leftCollapsed)}
                className="h-8 w-full flex items-center justify-center transition-colors border-b"
                style={{ borderColor: P.border, backgroundColor: P.panelBar }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = P.panelHi)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = P.panelBar)}
              >
                <svg
                  className="w-4 h-4 transition-transform duration-300"
                  style={{ color: P.muted, transform: leftCollapsed ? "rotate(180deg)" : "none" }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div
                className="flex-1 overflow-y-auto scrollbar-thin"
                style={{
                  padding: leftCollapsed
                    ? "12px 8px"
                    : serenity
                    ? "8px 16px 16px"
                    : "16px 16px",
                  backgroundColor: P.panel,
                }}
              >
                {sidebar}
              </div>
            </div>
            )}

            {/* Center Stage */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative w-full max-w-none" style={{ backgroundColor: P.root }}>
              {title && (hideLeftSidebar || !leftCollapsed) && (
                <div
                  className="h-10 flex items-center px-4 shrink-0"
                  style={{ backgroundColor: P.root }}
                >
                  <h2 className="font-serif text-lg font-semibold" style={{ color: P.titleText }}>
                    {title}
                  </h2>
                </div>
              )}
              <div className="flex-1 overflow-hidden flex flex-col" style={{ backgroundColor: P.root }}>
                {children}
              </div>
            </div>

            {/* Right Inspector */}
            {inspector && (
              <div
                className={`flex flex-col transition-all duration-700 ease-out overflow-hidden ${
                  serenity ? "border-l border-white/10" : "rounded-2xl shadow-lg"
                }`}
                style={{
                  width: rightCollapsed ? 0 : 320,
                  backgroundColor: P.panel,
                  border: serenity ? undefined : P.panelBorder,
                  boxShadow: P.panelShadow,
                  backdropFilter: P.blur,
                }}
              >
                {!rightCollapsed && (
                  <>
                    <div
                      className={`flex items-center justify-between px-3 shrink-0 ${
                        serenity ? "h-8" : "h-10 border-b"
                      }`}
                      style={{ borderColor: P.border, backgroundColor: P.panel }}
                    >
                      {!serenity && (
                      <span
                        className="text-xs font-semibold uppercase tracking-wider font-sans"
                        style={{ color: P.muted }}
                      >
                        Inspector
                      </span>
                      )}
                      {serenity && <span className="flex-1" aria-hidden />}
                      <button
                        onClick={() => setRightCollapsed(true)}
                        className="w-6 h-6 flex items-center justify-center rounded transition-colors"
                        style={{ backgroundColor: P.panelBar }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = P.panelHi)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = P.panelBar)}
                      >
                        <svg className="w-4 h-4" style={{ color: P.muted }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                    <div
                      className="flex-1 overflow-y-auto scrollbar-thin px-3 py-3"
                      style={{
                        paddingTop: serenity ? 8 : 12,
                        backgroundColor: P.panel,
                      }}
                    >
                      {inspector}
                    </div>
                  </>
                )}
              </div>
            )}
            {inspector && rightCollapsed && (
              <button
                onClick={() => setRightCollapsed(false)}
                className="w-10 h-10 self-center rounded-full flex items-center justify-center shadow-lg transition-colors shrink-0"
                style={{
                  backgroundColor: P.panel,
                  border: P.panelBorder,
                  boxShadow: P.panelShadow,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = P.panelHi)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = P.panel)}
              >
                <svg className="w-4 h-4" style={{ color: P.muted }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
          </div>

          {/* Bottom Bar */}
          {bottom && bottomDock ? (
            <div
              className={`shrink-0 px-2 pb-2 pt-0 overflow-x-auto overflow-y-hidden ${
                serenity ? "border-t border-white/10" : ""
              }`}
              style={
                serenity
                  ? { backgroundColor: "transparent" }
                  : {
                      backgroundColor: "rgba(13,13,13,0.55)",
                      backdropFilter: "blur(24px)",
                      borderTop: `1px solid ${accentEdge}`,
                      boxShadow: "0 -12px 32px rgba(0,0,0,0.4)",
                    }
              }
            >
              {bottom}
            </div>
          ) : bottom ? (
            <div
              className="shrink-0"
              style={{
                height: bottomCollapsed ? 28 : undefined,
                backgroundColor: bottomCollapsed ? "transparent" : undefined,
              }}
            >
              {bottomCollapsed ? (
                <button
                  onClick={() => setBottomCollapsed(false)}
                  className="w-full h-7 flex items-center justify-center gap-1.5"
                >
                  <div className="w-10 h-1 rounded-full" style={{ backgroundColor: learn ? "rgba(255,255,255,0.2)" : C.textLight }} />
                  <span
                    className="text-[9px] uppercase tracking-widest font-sans font-semibold"
                    style={{ color: learn ? P.muted : C.textMuted }}
                  >
                    Expandir
                  </span>
                </button>
              ) : (
                <div className="flex flex-col">
                  <button
                    onClick={() => setBottomCollapsed(true)}
                    className="w-full h-7 flex items-center justify-center gap-1.5 shrink-0 hover:opacity-70 transition-all duration-700"
                  >
                    <div className="w-10 h-1 rounded-full" style={{ backgroundColor: learn ? "rgba(255,255,255,0.2)" : C.textLight }} />
                    <span
                      className="text-[9px] uppercase tracking-widest font-sans font-semibold"
                      style={{ color: learn ? P.muted : C.textMuted }}
                    >
                      Colapsar
                    </span>
                    <svg
                      className="w-3 h-3"
                      style={{ color: learn ? P.muted : C.textMuted, transform: "rotate(180deg)" }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div
                    className={`px-2 pb-1 transition-all duration-700 ${learn ? "rounded-t-2xl pt-1" : ""}`}
                    style={
                      learn
                        ? {
                            backgroundColor: "rgba(13,13,13,0.55)",
                            backdropFilter: "blur(24px)",
                            border: `1px solid ${accentEdge}`,
                            boxShadow: "0 -16px 40px rgba(0,0,0,0.45)",
                          }
                        : undefined
                    }
                  >
                    {bottom}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <NeckEditorOverlay />
        </div>
      </WorkbenchContext.Provider>
    </NeckEditorProvider>
  );
};

export default WorkbenchLayout;
